import { useState, useEffect, useMemo } from 'react';
import { PlusCircle, Search, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEmailNotifications } from '../hooks/useEmailNotifications';
import { Transaction, DateRange, StatusFilterValue } from '../types/dashboard';
import { supabase } from '../lib/supabase';
import DashboardStats from '../components/dashboard/DashboardStats';
import TransactionList from '../components/dashboard/TransactionList';
import TransactionModal from '../components/transaction/TransactionModal';
import ModificationRequestModal from '../components/ModificationRequestModal';
import StatusFilter from '../components/dashboard/StatusFilter';
import DateFilter from '../components/dashboard/DateFilter';
import AgencyFilter from '../components/dashboard/AgencyFilter';
import { useTransactions } from '../hooks/useTransactions';
import ViewTransactionModal from '../components/validation/ViewTransactionModal';
import CompromiseModal from '../components/dashboard/CompromiseModal';
import RegulModal from '../components/dashboard/RegulModal';
import ActeModal from '../components/dashboard/ActeModal';
import EncaissementModal from '../components/dashboard/EncaissementModal';
import ClotureModal from '../components/dashboard/ClotureModal';
import CancelTransactionModal from '../components/dashboard/CancelTransactionModal';

export default function Dashboard() {
  const { user } = useAuth();
  const { sendStatusChangeNotification } = useEmailNotifications();
  const isAdmin = user?.role === 'admin';
  const isReadOnly = user?.role === 'lecture';
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCompromiseModalOpen, setIsCompromiseModalOpen] = useState(false);
  const [isRegulModalOpen, setIsRegulModalOpen] = useState(false);
  const [isActeModalOpen, setIsActeModalOpen] = useState(false);
  const [isEncaissementModalOpen, setIsEncaissementModalOpen] = useState(false);
  const [isClotureModalOpen, setIsClotureModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    // 3 mois en arrière
    const startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    // Fin du mois en cours
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  });
  
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>({
    potentiel: true,
    compromis: true,
    acte: true,
    encaisse: true,
    cloture: true
  });
  
  const [selectedAgencies, setSelectedAgencies] = useState<string[]>([]);

  const {
    transactions,
    filteredTransactions,
    loading,
    error,
    stats,
    sortField,
    sortDirection,
    handleSort,
    loadTransactions
  } = useTransactions(user, searchTerm, dateRange, statusFilter, selectedAgencies);

  // Get unique agencies from transactions
  const availableAgencies = useMemo(() => {
    const agencies = new Set<string>();
    transactions.forEach(t => t.agencies.forEach(a => agencies.add(a)));
    return Array.from(agencies).sort();
  }, [transactions]);

  // Close all modals
  const closeAllModals = () => {
    setIsModalOpen(false);
    setIsModificationModalOpen(false);
    setIsViewModalOpen(false);
    setIsCompromiseModalOpen(false);
    setIsRegulModalOpen(false);
    setIsActeModalOpen(false);
    setIsEncaissementModalOpen(false);
    setIsClotureModalOpen(false);
    setIsCancelModalOpen(false);
    setSelectedTransaction(null);
    setProcessing(false);
  };

  // Handle compromise transaction
  const handleCompromiseTransaction = async (transaction: Transaction, compromiseDate: string, expectedActeDate: string) => {
    if (!transaction || !user) {
      console.log('[Dashboard] Cannot process compromise: missing transaction or user');
      return;
    }

    console.log('[Dashboard] Starting compromise process for transaction:', transaction.property_number);
    console.log('[Dashboard] Compromise date:', compromiseDate);
    console.log('[Dashboard] Expected acte date:', expectedActeDate);
    
    try {
      setProcessing(true);

      // Update transaction status
      console.log('[Dashboard] Updating transaction status to compromis');
      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          status: 'compromis',
          compromise_date: compromiseDate,
          expected_acte_date: expectedActeDate
        })
        .eq('id', transaction.id);

      if (updateError) {
        console.error('[Dashboard] Error updating transaction:', updateError);
        throw updateError;
      }

      console.log('[Dashboard] Transaction updated successfully');

      // Envoyer une notification email pour le changement de statut
      try {
        console.log('[Dashboard] Sending status change notification email');
        await sendStatusChangeNotification(transaction.id, transaction.status, 'compromis');
        console.log('[Dashboard] Status change notification sent successfully');
      } catch (emailError) {
        console.error('Error sending status change notification:', emailError);
        // Ne pas faire échouer la mise à jour si l'email échoue
      }

      // Add transaction log
      console.log('[Dashboard] Adding transaction log');
      const { error: logError } = await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: transaction.id,
          user_id: user.id,
          event_type: 'compromise',
          event_data: {
            compromise_date: compromiseDate,
            expected_acte_date: expectedActeDate
          }
        }]);

      if (logError) {
        console.error('[Dashboard] Error adding log:', logError);
        // Don't throw here, log is not critical
      }

      console.log('[Dashboard] Log added successfully');

      // Reload transactions
      console.log('[Dashboard] Reloading transactions');
      await loadTransactions();
      
      console.log('[Dashboard] Compromise process completed successfully');
      
      // Close modals after successful update
      closeAllModals();
      
    } catch (err: any) {
      console.error('[Dashboard] Error in compromise process:', err);
      throw err; // Re-throw to let modal handle the error
    } finally {
      console.log('[Dashboard] Setting processing to false');
      setProcessing(false);
    }
  };

  const handleDeleteTransaction = async (transaction: Transaction) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transaction.id);
      if (error) throw error;
      await loadTransactions();
    } catch (err: any) {
      console.error('Error deleting transaction:', err);
    }
  };

  const handleCancel = async (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsCancelModalOpen(true);
  };

  const handleCancelTransaction = async (cancellationDate: string, cancellationReason: string) => {
    if (!selectedTransaction || !user) return;

    try {
      setProcessing(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({ 
          status: 'annule',
          cancelled_at: new Date().toISOString(),
          cancellation_date: cancellationDate,
          cancellation_reason: cancellationReason
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: selectedTransaction.id,
          user_id: user.id,
          event_type: 'cancellation',
          event_data: {
            previous_status: selectedTransaction.status,
            cancelled_at: new Date().toISOString(),
            cancellation_date: cancellationDate,
            cancellation_reason: cancellationReason
          }
        }]);

      await loadTransactions();
      closeAllModals();
    } catch (err: any) {
      console.error('Error canceling transaction:', err);
      setProcessing(false);
    }
  };

  const handleActe = async (acteDate: string, invoiceDate: string, invoiceNumber: string) => {
    if (!selectedTransaction || !user) return;

    try {
      setProcessing(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          status: 'acte',
          acte_date: acteDate,
          invoice_date: invoiceDate,
          invoice_number: invoiceNumber
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: selectedTransaction.id,
          user_id: user.id,
          event_type: 'acte',
          event_data: {
            acte_date: acteDate,
            invoice_date: invoiceDate,
            invoice_number: invoiceNumber
          }
        }]);

      await loadTransactions();
      closeAllModals();
    } catch (err: any) {
      console.error('Error updating transaction to acte:', err);
      setProcessing(false);
    }
  };

  const handleEncaissement = async (encaissementDate: string) => {
    if (!selectedTransaction || !user) return;

    try {
      setProcessing(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          status: 'encaisse',
          encaissement_date: encaissementDate
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: selectedTransaction.id,
          user_id: user.id,
          event_type: 'encaissement',
          event_data: {
            encaissement_date: encaissementDate
          }
        }]);

      await loadTransactions();
      closeAllModals();
    } catch (err: any) {
      console.error('Error updating transaction to encaisse:', err);
      setProcessing(false);
    }
  };

  const handleCloture = async () => {
    if (!selectedTransaction || !user) return;

    try {
      setProcessing(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({
          status: 'cloture'
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: selectedTransaction.id,
          user_id: user.id,
          event_type: 'cloture',
          event_data: {}
        }]);

      await loadTransactions();
      closeAllModals();
    } catch (err: any) {
      console.error('Error updating transaction to cloture:', err);
      setProcessing(false);
    }
  };

  const handleRegulRequest = async (comment: string) => {
    if (!selectedTransaction || !user) return;

    try {
      setProcessing(true);

      const { error: updateError } = await supabase
        .from('transactions')
        .update({ 
          regul: true,
          commentaire_regul: comment
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      const { error: requestError } = await supabase
        .from('modification_requests')
        .insert([{
          transaction_id: selectedTransaction.id,
          requested_by: user.id,
          status: 'pending',
          changes_requested: { message: comment },
          request_status: 'submitted'
        }]);

      if (requestError) throw requestError;

      await loadTransactions();
      closeAllModals();
    } catch (err: any) {
      console.error('Error requesting regul:', err);
      setProcessing(false);
    }
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsViewModalOpen(true);
  };

  return (
    <div className="space-y-6">

      {/* ── Page header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <PlusCircle size={22} className="text-white/80" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Tableau de bord</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{filteredTransactions.length}</span>
                {' '}transaction{filteredTransactions.length !== 1 ? 's' : ''} affichée{filteredTransactions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {!isReadOnly && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-900 bg-white hover:bg-indigo-50 shadow-md transition-all shrink-0"
            >
              <PlusCircle size={16} className="text-indigo-600" />
              <span>Nouvelle transaction</span>
            </button>
          )}
        </div>
      </div>

      <DashboardStats stats={stats} />

      {/* ── Filters card ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <SlidersHorizontal size={14} className="text-indigo-600" />
          </div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">Filtres</h2>
        </div>
        <div className="px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Rechercher une transaction…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
              />
            </div>
            <DateFilter value={dateRange} onChange={setDateRange} />
            <StatusFilter value={statusFilter} onChange={setStatusFilter} />
            <AgencyFilter
              agencies={availableAgencies}
              selectedAgencies={selectedAgencies}
              onChange={setSelectedAgencies}
            />
          </div>
        </div>
      </div>

      {/* ── Transaction table ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <TransactionList
            transactions={filteredTransactions}
            loading={loading}
            sortField={sortField}
            sortDirection={sortDirection}
            isAdmin={isAdmin}
            onSort={handleSort}
            onEdit={(transaction) => {
              setSelectedTransaction(transaction);
              setIsModalOpen(true);
            }}
            onDelete={handleDeleteTransaction}
            onValidate={() => {}}
            onModificationRequest={(transaction) => {
              setSelectedTransaction(transaction);
              setIsModificationModalOpen(true);
            }}
            onCancel={handleCancel}
            onCompromise={(transaction) => {
              console.log('Opening compromise modal for transaction:', transaction.property_number);
              setSelectedTransaction(transaction);
              setIsCompromiseModalOpen(true);
            }}
            onViewTransaction={handleViewTransaction}
            onActeTransaction={(transaction) => {
              setSelectedTransaction(transaction);
              setIsActeModalOpen(true);
            }}
            onEncaissementTransaction={(transaction) => {
              setSelectedTransaction(transaction);
              setIsEncaissementModalOpen(true);
            }}
            onClotureTransaction={(transaction) => {
              setSelectedTransaction(transaction);
              setIsClotureModalOpen(true);
            }}
            onRegulTransaction={(transaction) => {
              setSelectedTransaction(transaction);
              setIsRegulModalOpen(true);
            }}
          />
      </div>

      {/* Modals */}
      <TransactionModal
        transaction={selectedTransaction}
        isOpen={isModalOpen}
        onClose={closeAllModals}
        onSuccess={() => {
          loadTransactions();
          closeAllModals();
        }}
      />

      <ModificationRequestModal
        transaction={selectedTransaction}
        isOpen={isModificationModalOpen}
        onClose={closeAllModals}
        onSuccess={() => {
          loadTransactions();
          closeAllModals();
        }}
      />

      <ViewTransactionModal
        transaction={selectedTransaction}
        isOpen={isViewModalOpen}
        onClose={closeAllModals}
      />

      <CompromiseModal
        isOpen={isCompromiseModalOpen}
        onClose={() => {
          console.log('[Dashboard] Closing compromise modal');
          closeAllModals();
        }}
        onValidate={async (compromiseDate, expectedActeDate) => {
          console.log('[Dashboard] Compromise modal validation called');
          if (!selectedTransaction) {
            throw new Error('Aucune transaction sélectionnée');
          }
          await handleCompromiseTransaction(selectedTransaction, compromiseDate, expectedActeDate);
        }}
        disabled={processing}
      />

      <RegulModal
        transaction={selectedTransaction}
        isOpen={isRegulModalOpen}
        onClose={closeAllModals}
        onSubmit={handleRegulRequest}
      />

      <ActeModal
        isOpen={isActeModalOpen}
        onClose={closeAllModals}
        onValidate={handleActe}
      />

      <EncaissementModal
        isOpen={isEncaissementModalOpen}
        onClose={closeAllModals}
        onValidate={handleEncaissement}
      />

      <ClotureModal
        isOpen={isClotureModalOpen}
        onClose={closeAllModals}
        onValidate={handleCloture}
        transactionId={selectedTransaction?.id || ''}
        retrocessions={selectedTransaction?.retrocessions || []}
      />

      <CancelTransactionModal
        isOpen={isCancelModalOpen}
        onClose={closeAllModals}
        onValidate={handleCancelTransaction}
        transactionId={selectedTransaction?.id || ''}
      />
    </div>
  );
}